import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router-dom'

import { AppFreshnessGuard } from '@/components/guards/AppFreshnessGuard'
import AuthGuard from '@/components/guards/AuthGuard'
import { EnsureOrganization } from '@/components/guards/EnsureOrganization'
import { RouteAnalytics } from '@/components/guards/RouteAnalytics'
import { ScreenWakeLockManager } from '@/components/guards/ScreenWakeLockManager'
import AppLayout from '@/layouts/AppLayout'
import { Pages } from '@/routes/pages'
import { ROUTES } from '@/routes/paths'

/** The app's route tree. Exported apart from the router so routes/__tests__/handedOutLinks.test.ts can match on it. */
export const appRoutes: RouteObject[] = [
  {
    // Root layout route: fires analytics page views and checks app freshness on every
    // navigation, then renders the matched route. Pathless, so it contributes no URL
    // segment and does not affect route matching — it just guarantees these trackers
    // mount above all pages.
    element: (
      <>
        <RouteAnalytics />
        <AppFreshnessGuard />
        <ScreenWakeLockManager />
        <Outlet />
      </>
    ),
    children: [
      // Root → redirect authenticated users to org library, unauthenticated to login
      {
        path: ROUTES.ROOT.path,
        element: <AuthGuard mode={{ type: 'public' }} />,
        children: [
          {
            index: true,
            element: <Navigate to={ROUTES.AUTH.LOGIN.fullPath} replace />,
          },
        ],
      },

      // Public routes (/auth/*) — do NOT wrap with AppLayout
      // If user is already authenticated → redirect to organization library
      {
        path: ROUTES.AUTH.ROOT.path,
        element: <AuthGuard mode={{ type: 'public' }} />,
        children: [{ path: ROUTES.AUTH.LOGIN.path, element: <Pages.Login /> }],
      },

      {
        path: ROUTES.TERMS_OF_SERVICE.path,
        element: <Pages.TermsOfService />,
      },

      // Public support page — unguarded, like /terms-of-service, so a logged-out
      // visitor (or someone whose session expired) can still reach us by email.
      {
        path: ROUTES.CUSTOMER_SUPPORT.path,
        element: <Pages.CustomerSupport />,
      },

      // The B2B portal's copies of the two public pages: unguarded and 401-exempt like the original ones.
      { path: ROUTES.B2B.TERMS_OF_SERVICE.path, element: <Pages.TermsOfService /> },
      { path: ROUTES.B2B.CUSTOMER_SUPPORT.path, element: <Pages.CustomerSupport /> },
      // A workflow's invitation page: public and unguarded, since the learner starts the workflow in their AI
      // assistant and never signs in here. 401-exempt in STATUS_REDIRECT_RULES, so its code is never saved.
      { path: ROUTES.B2B.INVITE.path, element: <Pages.B2bInvite /> },

      // Invite-link landing — a transient LOADING/consume page (renders <LoadingScreen/>),
      // NOT an auth page. It captures the token from the URL fragment, strips the URL,
      // and either redeems (if logged in) or hands off to the NORMAL login page — exactly
      // like CloneWorkspace. It renders NO auth UI of its own. PUBLIC and unguarded so it
      // also renders for logged-OUT users (to capture + stash the token before sending
      // them to login); a guard is avoided because the public AuthGuard would auto-navigate
      // an already-logged-in user away before redeem. Also 401-exempt via
      // STATUS_REDIRECT_RULES in api/setup.ts so the boot getMe() 401 can't auto-save the
      // token-bearing URL. After login, the AuthGuard `invite-link` classifier variant
      // returns the user here to redeem the token still in sessionStorage.
      {
        path: ROUTES.INVITE.path,
        element: <Pages.Invite />,
      },

      {
        path: ROUTES.API_DOCS.path,
        element: <Pages.ApiDocs />,
      },

      // Onboarding routes (e.g., Terms of Service, Organization Invite) — authenticated, but do NOT wrap with AppLayout
      {
        path: ROUTES.ROOT.path,
        element: <AuthGuard mode={{ type: 'onboarding' }} />,
        children: [
          { path: ROUTES.ACCEPT_TERMS_OF_SERVICE.path, element: <Pages.AcceptTermsOfService /> },
          { path: ROUTES.ORGANIZATION_INVITE.path, element: <Pages.OrganizationInvite /> },
          { path: ROUTES.ONBOARDING.path, element: <Pages.Onboarding /> },
        ],
      },

      // Main app routes (after completing onboarding)
      {
        path: ROUTES.ROOT.path,
        element: <AuthGuard mode={{ type: 'protected' }} />,
        children: [
          // Clone workspace route — outside AppLayout (transient action page)
          { path: ROUTES.CLONE_WORKSPACE.path, element: <Pages.CloneWorkspace /> },
          { path: ROUTES.CLONE_WORKSPACE_SCENARIO.path, element: <Pages.CloneWorkspace /> },
          {
            element: <AppLayout />,
            children: [
              { path: ROUTES.WORKSPACE_LIBRARY.path, element: <Pages.ScenarioLibrary /> },
              { path: ROUTES.CREATE_SCENARIO.path, element: <Pages.CreateScenario /> },
              { path: ROUTES.EDIT_SCENARIO.path, element: <Pages.EditExistingScenario /> },
              { path: ROUTES.SCENARIO_PLAYBACK.path, element: <Pages.ScenarioPlayback /> },
              // The B2B portal (/work): the same guard and layout, its own sidebar, never the original workspaces.
              { path: ROUTES.B2B.HOME.path, element: <Pages.B2bHome /> },
              { path: ROUTES.B2B.WORKSPACE_WORKFLOWS.path, element: <Pages.B2bWorkspaceWorkflows /> },
              { path: ROUTES.B2B.WORKFLOW.path, element: <Pages.B2bWorkflow /> },
              { path: ROUTES.B2B.WORKFLOW_SESSION.path, element: <Pages.B2bWorkflow /> },
              { path: ROUTES.B2B.SIMULATION.path, element: <Pages.B2bSimulation /> },
              { path: ROUTES.B2B.ACCOUNT.path, element: <Pages.AccountSettings /> },
              { path: ROUTES.B2B.WORKSPACE_MEMBERS.path, element: <Pages.B2bWorkspaceMembers /> },
              { path: ROUTES.B2B.WORKSPACE_SETUP.path, element: <Pages.B2bWorkspaceSetup /> },
              { path: ROUTES.B2B.WORKSPACE_INVITATION_ACCEPT.path, element: <Pages.B2bWorkspaceInvitation /> },
              { path: ROUTES.B2B.WORKSPACE_INVITATION_DECLINE.path, element: <Pages.B2bWorkspaceInvitation /> },
              { path: ROUTES.B2B.STAFF.path, element: <Pages.B2bStaffConsole /> },
              { path: ROUTES.B2B.STAFF_WORKSPACE.path, element: <Pages.B2bStaffConsole /> },
              { path: ROUTES.B2B.STAFF_WORKFLOW.path, element: <Pages.B2bStaffConsole /> },
              { path: ROUTES.B2B.STAFF_SESSION.path, element: <Pages.B2bStaffConsole /> },
              { path: ROUTES.B2B.STAFF_UNKNOWN.path, element: <Pages.B2bStaffConsole /> },
              { path: ROUTES.B2B.NOT_FOUND.path, element: <Pages.B2bNotFound /> },
              { path: ROUTES.SESSION_FEEDBACK.path, element: <Pages.SessionFeedback /> },
              { path: ROUTES.SESSION_FEEDBACK_REGENERATE.path, element: <Pages.RegenerateFeedback /> },
              { path: ROUTES.SESSION_KEY_MOMENTS.path, element: <Pages.SessionKeyMoments /> },
              { path: ROUTES.MY_PATH.path, element: <Pages.MyPath /> },
              { path: ROUTES.MY_PATH_DEBUG.path, element: <Pages.MyPathDebug /> },
              { path: ROUTES.SESSION_SIGNALS.path, element: <Pages.SessionSignals /> },
              { path: ROUTES.ANALYTICS.path, element: <Pages.Analytics /> },
              { path: ROUTES.WORKSPACE_MEMBERS.path, element: <Pages.WorkspaceMembers /> },
              { path: ROUTES.INVITE_LINKS.path, element: <Pages.InviteLinks /> },
              { path: ROUTES.WORKSPACE_SETTINGS.path, element: <Pages.WorkspaceSettings /> },
              { path: ROUTES.ACCOUNT_SETTINGS.path, element: <Pages.AccountSettings /> },
              // Catch-all for authenticated users: resolve org and redirect to library
              { path: '*', element: <EnsureOrganization /> },
            ],
          },
        ],
      },

      // Catch-all fallback for unauthenticated users → redirect to login
      { path: '*', element: <Navigate to={ROUTES.AUTH.LOGIN.fullPath} replace /> },
    ],
  },
]

const router = createBrowserRouter(appRoutes)

export default router
